import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { formatDate } from "../../util/date.ts";

interface InlineDatePickerProps {
  value: Date | null;
  onChange: (date: Date) => void;
  onSave?: () => void;
  onCancel?: () => void;
  placeholder?: string;
  displayClassName?: string;
  formatDisplay?: (date: Date | null) => string;
  isSimple?: boolean;
  alignRight?: boolean;
}

const InlineDatePicker: React.FC<InlineDatePickerProps> = ({
  value,
  onChange,
  onSave,
  onCancel,
  placeholder = "Click to select date",
  displayClassName = "",
  isSimple = false,
  alignRight = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(
    value
      ? new Date(value.getFullYear(), value.getMonth(), 1)
      : new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [calendarPosition, setCalendarPosition] = useState<{
    top: number;
    left?: number;
    right?: number;
  }>({ top: 0, left: 0 });

  const isOpen = isSimple ? calendarOpen : isEditing;

  // Update calendar position when opened
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const calendarWidth = 320; // w-80 = 20rem = 320px
      const calendarHeight = 400; // approximate height
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Use viewport coordinates directly for better mobile compatibility
      let top = rect.bottom + 4;

      // Prevent vertical overflow - show above trigger if needed
      if (top + calendarHeight > viewportHeight) {
        top = rect.top - calendarHeight - 4;
      }

      if (alignRight) {
        let right = viewportWidth - rect.right;
        // Prevent horizontal overflow from right
        if (right < 8) {
          right = 8;
        }
        setCalendarPosition({ top, right });
      } else {
        let left = rect.left;
        // Prevent horizontal overflow
        if (left + calendarWidth > viewportWidth) {
          left = Math.max(8, viewportWidth - calendarWidth - 8);
        }
        if (left < 8) {
          left = 8;
        }
        setCalendarPosition({ top, left });
      }
    }
  }, [isOpen, alignRight]);

  const handleSave = () => {
    onSave?.();
    setIsEditing(false);
  };

  // Close calendar when clicking outside
  useEffect(() => {
    const handleCancel = () => {
      onCancel?.();
      setIsEditing(false);
      setCalendarOpen(false);
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is inside the calendar container
      if (containerRef.current && containerRef.current.contains(target)) {
        return;
      }

      // For portaled content, also check if click is inside the calendar element itself
      const calendarElement = (target as Element).closest?.(
        ".fixed.bg-white.border.border-gray-300.rounded-lg.shadow-lg",
      );
      if (calendarElement) {
        return;
      }

      handleCancel();
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onCancel, value]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek };
  };

  const handleDateClick = (day: number) => {
    const newDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );
    onChange(newDate);
    if (isSimple) {
      onChange(newDate);
      setCalendarOpen(false);
    }
  };

  const handlePrevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1),
    );
  };

  const handleNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1),
    );
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
  const monthYear = currentMonth.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
  });

  const isSelectedDate = (day: number) => {
    if (!value) return false;
    return (
      value.getDate() === day &&
      value.getMonth() === currentMonth.getMonth() &&
      value.getFullYear() === currentMonth.getFullYear()
    );
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === currentMonth.getMonth() &&
      today.getFullYear() === currentMonth.getFullYear()
    );
  };

  // Simple mode - just show the trigger and calendar on click, no save/cancel needed
  if (isSimple) {
    return (
      <>
        <div
          ref={triggerRef}
          onClick={() => setCalendarOpen(true)}
          className={`cursor-pointer hover:bg-gray-100 p-1 rounded transition-colors ${displayClassName}`}
        >
          {value ? (
            formatDate(value, "24h", true)
          ) : (
            <span className="text-gray-400 italic">{placeholder}</span>
          )}
        </div>
        {calendarOpen &&
          createPortal(
            <div ref={containerRef}>
              <div
                className="fixed bg-white border border-gray-300 rounded-lg shadow-lg p-3 sm:p-4 w-72 sm:w-80"
                style={{
                  top: `${calendarPosition.top}px`,
                  ...(calendarPosition.left !== undefined
                    ? { left: `${calendarPosition.left}px` }
                    : { right: `${calendarPosition.right}px` }),
                  zIndex: 9999,
                }}
              >
                {/* Month/Year header with navigation */}
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <button
                    type="button"
                    onClick={handlePrevMonth}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <span className="font-semibold text-sm sm:text-base text-gray-700">
                    {monthYear}
                  </span>
                  <button
                    type="button"
                    onClick={handleNextMonth}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>

                {/* Weekday headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                    <div
                      key={day}
                      className="text-center text-xs font-semibold text-gray-500 py-1"
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar days */}
                <div className="grid grid-cols-7 gap-1">
                  {/* Empty cells for days before the first of the month */}
                  {Array.from({ length: startingDayOfWeek }).map((_, index) => (
                    <div key={`empty-${index}`} className="aspect-square" />
                  ))}

                  {/* Actual days */}
                  {Array.from({ length: daysInMonth }).map((_, index) => {
                    const day = index + 1;
                    const selected = isSelectedDate(day);
                    const today = isToday(day);

                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => handleDateClick(day)}
                        className={`aspect-square flex items-center justify-center rounded text-sm ${
                          selected
                            ? "bg-info text-white font-semibold"
                            : today
                              ? "bg-gray-100 font-semibold text-info"
                              : "hover:bg-gray-50"
                        }`}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>,
            document.body,
          )}
      </>
    );
  }

  if (!isEditing) {
    return (
      <div
        ref={triggerRef}
        onClick={() => setIsEditing(true)}
        className={`cursor-pointer hover:bg-secondary-light p-2 rounded transition-colors ${displayClassName}`}
      >
        {value ? (
          formatDate(value, "24h", true)
        ) : (
          <span className="text-gray-400 italic">{placeholder}</span>
        )}
      </div>
    );
  }

  return (
    <>
      {isOpen &&
        createPortal(
          <div ref={containerRef}>
            <div
              className="fixed bg-white border border-gray-300 rounded-lg shadow-lg p-3 sm:p-4 w-72 sm:w-80"
              style={{
                top: `${calendarPosition.top}px`,
                ...(calendarPosition.left !== undefined
                  ? { left: `${calendarPosition.left}px` }
                  : { right: `${calendarPosition.right}px` }),
                zIndex: 9999,
              }}
            >
              {/* Month/Year header with navigation */}
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <button
                  type="button"
                  onClick={handlePrevMonth}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <span className="font-semibold text-sm sm:text-base text-gray-700">
                  {monthYear}
                </span>
                <button
                  type="button"
                  onClick={handleNextMonth}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>

              {/* Weekday headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                  <div
                    key={day}
                    className="text-center text-xs font-semibold text-gray-500 py-1"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar days */}
              <div className="grid grid-cols-7 gap-1">
                {/* Empty cells for days before the first of the month */}
                {Array.from({ length: startingDayOfWeek }).map((_, index) => (
                  <div key={`empty-${index}`} className="aspect-square" />
                ))}

                {/* Actual days */}
                {Array.from({ length: daysInMonth }).map((_, index) => {
                  const day = index + 1;
                  const selected = isSelectedDate(day);
                  const today = isToday(day);

                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => handleDateClick(day)}
                      className={`aspect-square flex items-center justify-center rounded text-sm ${
                        selected
                          ? "bg-info text-white font-semibold"
                          : today
                            ? "bg-gray-100 font-semibold text-info"
                            : "hover:bg-gray-50"
                      }`}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>

              {/* Action buttons */}
              <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleSave}
                  className="px-3 py-1 rounded bg-success text-white hover:bg-success-dark transition-colors text-sm"
                  disabled={!value}
                >
                  Save
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export { InlineDatePicker };
